import React, { SetStateAction, Dispatch } from 'react'
import { marketingOverviewFilter } from '@dataTypes/marketing.type'
import './CampaignOverviewStyle.css'
import useCampaignOverview from './useCampaignOverview'
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns'
import BouncedMessageModal from '@components/layout/Modals/BouncedMessage/BouncedMessageModal.component'
import OverviewFilter from '../Filter/OverviewFilter.component'

export interface CampaignOverviewProps {
  setSelectedDropdownCampaignId?: Dispatch<SetStateAction<string>>
  showOverviewDropdown?: boolean
  /** Hide the “Campaign Overview” heading (e.g. on Marketing → Campaign list tab). */
  hideTitle?: boolean
  /** When set (with setDateFilter), period is shared with Marketing Overview analytics */
  dateFilter?: marketingOverviewFilter
  setDateFilter?: Dispatch<SetStateAction<marketingOverviewFilter>>
  showFilter?: boolean
}

const CampaignOverview: React.FC<CampaignOverviewProps> = ({
  setSelectedDropdownCampaignId,
  showOverviewDropdown = false,
  hideTitle = false,
  dateFilter: controlledDateFilter,
  setDateFilter: controlledSetDateFilter,
  showFilter = false,
}) => {
  const hooks = useCampaignOverview(
    setSelectedDropdownCampaignId,
    controlledDateFilter,
    controlledSetDateFilter,
  )

  return (
    <>
      <div className="mo-campaign">
        <div
          className={
            hideTitle
              ? 'mo-campaign-header-row mo-campaign-header-row--no-title'
              : 'mo-campaign-header-row'
          }
        >
          {!hideTitle && <span className="mo-header">Campaign overview</span>}
          <div className="mo-campaign-header-actions">
            {showOverviewDropdown && (
              <span className="mo-campaign-dropdown-host">
                <DropDownListComponent
                  cssClass="campaign-overview mo-campaign-overview-ddl"
                  dataSource={hooks.campaignDropdownOptions}
                  placeholder="Select campaign"
                  value={
                    hooks.selectedCampaign.text === 'Select campaign'
                      ? null
                      : hooks.selectedCampaign.text
                  }
                  change={hooks.onDropDownSelect}
                  popupHeight="240px"
                />
              </span>
            )}
            {showFilter && (
              <OverviewFilter
                activeFilter={hooks.dateFilter}
                onRefresh={hooks.handleRefresh}
                setFilter={hooks.setDateFilter}
              />
            )}
          </div>
        </div>
        <div className="mo-co-board">
          <div className="mo-co-board-data">
            <div className="mo-co-board-item">
              <span className="mo-co-header">
                Message sent
                <i
                  className="mgc_information_fill"
                  data-bs-toggle="tooltip"
                  title="Total number of outbound messages attempted in the campaign."
                />
              </span>
              <div className="mo-co-count">
                <span>{hooks.messageSent.current}</span>
                {hooks.messageSent.status !== 'neutral' && (
                  <span
                    className={`mo-co-count-badge ${hooks.messageSent.status}`}
                  >
                    {hooks.messageSent.status === 'increase' ? '+' : '-'}
                    {hooks.messageSent.percentage}%
                  </span>
                )}
              </div>
              {hooks.messageSent.status !== 'neutral' && (
                <span className="mo-co-previous">
                  Previously {hooks.messageSent.previous}
                </span>
              )}
            </div>
            <div className="mo-co-board-item">
              <span className="mo-co-header">
                Delivered
                <i
                  className="mgc_information_fill"
                  data-bs-toggle="tooltip"
                  title="Messages that were successfully delivered to recipients."
                />
              </span>
              <div className="mo-co-count">
                <span>{hooks.delivered.current}</span>
                {hooks.delivered.status !== 'neutral' && (
                  <span
                    className={`mo-co-count-badge ${hooks.delivered.status}`}
                  >
                    {hooks.delivered.status === 'increase' ? '+' : '-'}
                    {hooks.delivered.percentage}%
                  </span>
                )}
              </div>
              {hooks.delivered.status !== 'neutral' && (
                <span className="mo-co-previous">
                  Previously {hooks.delivered.previous}
                </span>
              )}
            </div>
            <div className="mo-co-board-item">
              <span className="mo-co-header">
                Delivery rate
                <i
                  className="mgc_information_fill"
                  data-bs-toggle="tooltip"
                  title="The percentage of sent messages that were successfully
                    delivered."
                />
              </span>
              <div className="mo-co-count">
                <span>{hooks.deliveryRate.current}%</span>
                {hooks.deliveryRate.status !== 'neutral' && (
                  <span
                    className={`mo-co-count-badge ${hooks.deliveryRate.status}`}
                  >
                    {hooks.deliveryRate.status === 'increase' ? '+' : '-'}
                    {hooks.deliveryRate.percentage}%
                  </span>
                )}
              </div>
              {hooks.deliveryRate.status !== 'neutral' && (
                <span className="mo-co-previous">
                  Previously {hooks.deliveryRate.previous}%
                </span>
              )}
            </div>
            <div className="mo-co-board-item">
              <span className="mo-co-header">
                Bounced
                <i
                  className="mgc_information_fill"
                  data-bs-toggle="tooltip"
                  title="Messages that failed to reach recipients."
                />
              </span>
              <div className="mo-co-count">
                <button
                  type="button"
                  className="unstyled-button"
                  onClick={hooks.onClickBouncedAnalytics}
                >
                  {hooks.bounced.current}
                </button>
                {hooks.bounced.status !== 'neutral' && (
                  <span className={`mo-co-count-badge ${hooks.bounced.status}`}>
                    {hooks.bounced.status === 'increase' ? '+' : '-'}
                    {hooks.bounced.percentage}%
                  </span>
                )}
              </div>
              {hooks.bounced.status !== 'neutral' && (
                <span className="mo-co-previous">
                  Previously {hooks.bounced.previous}
                </span>
              )}
            </div>
            <div className="mo-co-board-item">
              <span className="mo-co-header">
                Replies
                <i
                  className="mgc_information_fill"
                  data-bs-toggle="tooltip"
                  title="Number of replies received from recipients in response to
                    your campaign messages."
                />
              </span>
              <div className="mo-co-count">
                <span>{hooks.replies.current}</span>
                {hooks.replies.status !== 'neutral' && (
                  <span className={`mo-co-count-badge ${hooks.replies.status}`}>
                    {hooks.replies.status === 'increase' ? '+' : '-'}
                    {hooks.replies.percentage}%
                  </span>
                )}
              </div>
              {hooks.replies.status !== 'neutral' && (
                <span className="mo-co-previous">
                  Previously {hooks.replies.previous}
                </span>
              )}
            </div>
            <div className="mo-co-board-item">
              <span className="mo-co-header">
                Scheduled
                <i
                  className="mgc_information_fill"
                  data-bs-toggle="tooltip"
                  title="Messages queued to be sent at a future scheduled time."
                />
              </span>
              <div className="mo-co-count">
                <span>{hooks.scheduled.current}</span>
                {hooks.scheduled.status !== 'neutral' && (
                  <span
                    className={`mo-co-count-badge ${hooks.scheduled.status}`}
                  >
                    {hooks.scheduled.status === 'increase' ? '+' : '-'}
                    {hooks.scheduled.percentage}%
                  </span>
                )}
              </div>
              {hooks.scheduled.status !== 'neutral' && (
                <span className="mo-co-previous">
                  Previously {hooks.scheduled.previous}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {hooks.showBouncedModal && (
        <BouncedMessageModal
          data={hooks.filteredMessages}
          filterSelectionList={hooks.filterSelectionList}
          show={hooks.showBouncedModal}
          onHide={hooks.onHideBouncedModal}
        />
      )}
    </>
  )
}

export default CampaignOverview
